import React, { useContext, useEffect, useState } from "react";
import IProcessed_Report from "../../../../models/IProcessed_Report";
import UserService from "../../../../services/UserService";
import UserContext from "../../../../contexts/UserContext";
import SupplierContext from "../../../../contexts/SupplierContext";
import IOwDivTableCols from "../../../shared/OwDivTable/IOwDivTableCols";
import ExcelJS from "exceljs/dist/exceljs.min.js";
import OwButton from "../../../shared/OwButton/OwButton";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import { OwDivTable } from "../../../shared/OwDivTable/OwDivTable";
import IOnGetInventoryStockHistoryTransactionReportDTO from "../../../../models/DTO/IOnGetInventoryStockHistoryTransactionReportDTO";
import InventoryOperationEnum from "../../../../models/InventoryOperationEnum";

type Props = {
    processedReport: IProcessed_Report;
};

export const InventoryStockHistoryTransactionReport = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);

    const getInventoryOperationName = (operation: InventoryOperationEnum, jsx = true) => {
        switch (operation) {
            case InventoryOperationEnum.ADD:
                if (jsx) {
                    return <OwLabel title="add" />;
                } else {
                    return "Add";
                }
                break;
            case InventoryOperationEnum.SUBTRACT:
                if (jsx) {
                    return <OwLabel title="subtract" />;
                } else {
                    return "Subtract";
                }
                break;
            case InventoryOperationEnum.CYCLE_COUNT:
                if (jsx) {
                    return <OwLabel title="cycle_count" />;
                } else {
                    return "Cycle Count";
                }
                break;
        }
    };

    const processQty = (item: any) => {
        if (item.operation === InventoryOperationEnum.SUBTRACT) {
            if (item.qty > 0) {
                return `-${item.qty}`;
            } else {
                return item.qty;
            }
        } else {
            return item.qty;
        }
    };

    const getTableCols = (): IOwDivTableCols[] => {
        return [
            {
                title: "name",
                renderFn: (item) => item.name,
                className: "w-10",
            },
            {
                title: "sku",
                renderFn: (item) => item.sku,
                className: "w-10",
            },
            {
                title: "operation",
                renderFn: (item) => getInventoryOperationName(item.operation),
                className: "w-10",
            },
            {
                title: "qty",
                renderFn: (item) => processQty(item),
                className: "w-10",
            },
            {
                title: "created_at",
                renderFn: (item) => UserService.parseLastUpdated(item.created_at),
                className: "w-10",
            },
            {
                title: "user",
                renderFn: (item) => item.user,
                className: "w-10",
            },
            {
                title: "note",
                renderFn: (item) => item.note,
                className: "w-20",
            },
            {
                title: "order",
                renderFn: (item) => item.order,
                className: "w-10",
            },
            {
                title: "warehouse",
                renderFn: (item) => item.warehouse,
                className: "w-10",
            },
        ];
    };

    const [reportData, setReportData] = useState<IOnGetInventoryStockHistoryTransactionReportDTO>();
    const [tableCols, setTableCols] = useState<IOwDivTableCols[]>(getTableCols());

    const onGetInventoryStockHistoryTransactionReport = (data: IOnGetInventoryStockHistoryTransactionReportDTO) => {
        console.log("onGetInventoryStockHistoryTransactionReport: ", data);
        setReportData(data);
    };

    useEffect(() => {
        onGetInventoryStockHistoryTransactionReport(props.processedReport.report_data);
    }, []);

    const downloadExcel = () => {
        if (reportData) {
            const workbook = new ExcelJS.Workbook();
            const ws = workbook.addWorksheet(`Inventory Stock History Transaction Report`);

            const salesPeopleWs: { salesperson: string; ws: ExcelJS.Worksheet }[] = [];

            const columns: (
                | { header: string; key: string }
                | { header: string; key: string }
                | { header: string; key: string }
                | { header: string; key: string }
                | { header: string; key: string }
                | { header: string; key: string }
                | { header: string; key: string }
                | { header: string; key: string }
            )[] = [
                { header: "Name", key: "name" },
                { header: "SKU", key: "sku" },
                { header: "Operation", key: "operation" },
                { header: "Qty", key: "qty" },
                { header: "Created At", key: "createdAt" },
                { header: "User", key: "user" },
                { header: "Order", key: "order" },
                { header: "Note", key: "note" },
            ];

            ws.columns = columns;

            ws.getCell("A1").font = {
                bold: true,
            };

            ws.getCell("B1").font = {
                bold: true,
            };

            ws.getCell("C1").font = {
                bold: true,
            };

            ws.getCell("D1").font = {
                bold: true,
            };

            ws.getCell("E1").font = {
                bold: true,
            };

            ws.getCell("F1").font = {
                bold: true,
            };

            ws.getCell("G1").font = {
                bold: true,
            };

            ws.getCell("H1").font = {
                bold: true,
            };

            for (const item of reportData.data) {
                const name = item.name;
                const sku = item.sku;
                const note = item.note;
                const operation = getInventoryOperationName(item.operation, false);
                const user = item.user;
                const createdAt = item.created_at;
                const qty = processQty(item);
                const order = item.order;

                ws.addRow({ name, sku, note, operation, user, createdAt, qty, order });
            }

            // const periodStart = moment(reportData.periodStart).format("YYYY-MM-DD");
            // const periodEnd = moment(reportData.periodEnd).format("YYYY-MM-DD");

            workbook.xlsx.writeBuffer().then((data) => {
                const blob = new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
                const url = window.URL.createObjectURL(blob);
                const anchor = document.createElement("a");
                anchor.href = url;
                anchor.download = `orderwerks-inventory-stock-history-transaction-${UserService.parseLastUpdated(
                    props.processedReport.created_at
                )}.xlsx`;
                anchor.click();
                window.URL.revokeObjectURL(url);
            });
        }
    };

    return (
        <div className={"InventoryStockHistoryTransactionReport"}>
            {reportData && reportData.data.length > 0 ? <OwButton title={"export_to_excel"} fn={downloadExcel} /> : null}
            <div>
                <OwLabel title={"total_records"} postText={":"} isBold={true} className={"me-2 mb-2 mt-2"} />
                {reportData?.data.length}
                <div>
                    <OwLabel title={"created_at"} className={"me-2"} postText={":"} />
                    {UserService.parseLastUpdated(props.processedReport.created_at)}
                </div>
            </div>

            <div className={"mb-5"}>
                <OwDivTable cols={tableCols} data={reportData?.data || []} rowClassName={"border-bottom"} />
            </div>
        </div>
    );
};
